import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { dashboardService, DashboardData, DashboardKPI } from '../services/dashboard';
import { MonthPicker } from '../components/ui/MonthPicker';
import { StatusBadge } from '../components/ui/StatusBadge';
import { formatCurrency, formatDateBR } from '../utils/format';

const nomeMeses: Record<string, string> = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr', '05': 'Mai', '06': 'Jun',
  '07': 'Jul', '08': 'Ago', '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez', '00': 'Ano',
};

export const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [anoSelecionado, mesSelecionado] = periodo.split('-');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await dashboardService.getSummary(mesSelecionado, anoSelecionado);
        setData(response);
      } catch (error) {
        console.error('Erro ao buscar dados do dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [mesSelecionado, anoSelecionado]);

  if (loading) {
    return (
      <Layout pageTitle="Painel Financeiro" breadcrumb="Visão Geral">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Carregando dados...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) return null;

  return (
    <Layout pageTitle="Painel Financeiro" breadcrumb="Visão Geral">
      <div className="space-y-6 md:space-y-8 animate-fade-in-up">
        {/* Welcome + Month Selector */}
        <section className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white italic">DASHBOARD</h2>
            <p className="text-slate-500 mt-1 text-sm md:text-base">
              Bem-vindo(a), acompanhe o panorama mensal.
            </p>
            <p className="text-[11px] text-slate-600 mt-1 italic">
              ⓘ Cada KPI mostra <strong>previsto</strong> (todos os lançamentos) e <strong>realizado</strong> (apenas pagos). Cancelados são excluídos.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <MonthPicker value={periodo} onChange={setPeriodo} />
          </div>
        </section>

        {/* KPI Cards — Previsto vs Realizado */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiDualCard
            label="Receitas"
            kpi={data.receitas}
            icon="trending_up"
            accentColor="emerald"
          />
          <KpiDualCard
            label="Despesas"
            kpi={data.despesas}
            icon="trending_down"
            accentColor="rose"
          />
          <KpiDualCard
            label="Resultado"
            kpi={data.saldo}
            icon="stars"
            accentColor="primary"
            highlight
          />
        </section>

        {/* Movimentações do Mês */}
        <div className="glass-panel p-6 rounded-2xl border-slate-800/50 hover:border-slate-700/50 transition-all duration-500">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                Movimentações de {nomeMeses[mesSelecionado]}
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Lista de lançamentos do período (cancelados excluídos).
              </p>
            </div>
            {data.movimentacoes.some(m => m.status !== 'pago') && (
              <div className="w-2 h-2 rounded-full bg-rose-500/80 shadow-[0_0_8px_rgba(244,63,94,0.6)]"></div>
            )}
          </div>

          {data.movimentacoes.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-4xl text-slate-700 mb-2">event_busy</span>
              <p className="text-slate-500 text-sm">Nenhuma movimentação encontrada para este mês.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.movimentacoes.map((mov) => (
                <div
                  key={`${mov.tipo}-${mov.id}`}
                  className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-1 rounded-md ${mov.tipo === 'receita' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                      <span className="material-symbols-outlined text-[18px]">
                        {mov.tipo === 'receita' ? 'trending_up' : 'trending_down'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-black leading-tight line-clamp-1 ${mov.status === 'pago' ? 'text-slate-500 line-through' : 'text-slate-100'}`}>
                        {mov.descricao || 'Sem descrição'}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[12px]">
                          {mov.tipo === 'receita' ? 'person' : 'storefront'}
                        </span>
                        <span>{formatDateBR(mov.data)}</span>
                        <span className="text-slate-700">•</span>
                        <span className="truncate">
                          {mov.entidade_nome || (mov.tipo === 'receita' ? 'Sem cliente' : 'Sem fornecedor')}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-sm font-black ${
                      mov.tipo === 'receita'
                        ? 'text-emerald-400'
                        : (mov.status === 'pago' ? 'text-slate-500' : 'text-rose-400')
                    }`}>
                      {mov.tipo === 'receita' ? '+ ' : '- '}{formatCurrency(mov.valor)}
                    </span>
                    <StatusBadge status={mov.status} dotOnly />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

/* ============================================================================
 * Sub-componente: KPI duplo (previsto + realizado)
 * ============================================================================ */
interface KpiDualCardProps {
  label: string;
  kpi: DashboardKPI;
  icon: string;
  accentColor: 'emerald' | 'rose' | 'primary';
  highlight?: boolean;
}

const KpiDualCard: React.FC<KpiDualCardProps> = ({ label, kpi, icon, accentColor, highlight = false }) => {
  const accent = {
    emerald: { iconColor: 'text-emerald-400', barColor: 'bg-emerald-500', borderColor: '' },
    rose: { iconColor: 'text-rose-400', barColor: 'bg-rose-500', borderColor: '' },
    primary: { iconColor: 'text-primary', barColor: 'bg-primary', borderColor: highlight ? 'border-primary/30 bg-primary/5' : '' },
  }[accentColor];

  const previsto = kpi?.previsto ?? 0;
  const realizado = kpi?.realizado ?? 0;
  const pct = previsto !== 0 ? Math.min(100, Math.abs((realizado / previsto) * 100)) : 0;

  // Para resultado (que pode ser negativo) mostra valor absoluto
  const isNegativeResult = highlight && previsto < 0;

  return (
    <div className={`glass-panel p-5 rounded-xl flex flex-col gap-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group ${accent.borderColor}`}>
      <div className="flex justify-between items-start">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-slate-300 transition-colors">
          {label}
        </span>
        <div className={`bg-slate-800/50 border border-slate-700/50 ${accent.iconColor} p-1.5 rounded-lg`}>
          <span className="material-symbols-outlined text-[16px]">{icon}</span>
        </div>
      </div>

      {/* Previsto = informação primária */}
      <div>
        <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isNegativeResult ? 'text-rose-500' : 'text-slate-500'}`}>
          Previsto
        </p>
        <h3 className={`text-2xl font-black ${highlight && previsto < 0 ? 'text-rose-400' : highlight ? 'text-emerald-400' : 'text-white'}`}>
          {formatCurrency(previsto)}
        </h3>
      </div>

      {/* Barra de progresso de realização */}
      {previsto > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
            <span className="text-slate-500">Realizado</span>
            <span className="text-slate-400">{pct.toFixed(0)}%</span>
          </div>
          <div className="h-1 bg-slate-800/60 rounded-full overflow-hidden">
            <div className={`h-full ${accent.barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-sm font-black text-slate-200 mt-1">{formatCurrency(realizado)}</p>
        </div>
      )}
      {previsto <= 0 && (
        <div className="space-y-1.5 opacity-50">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
            <span className="text-slate-500">Realizado</span>
          </div>
          <p className="text-sm font-black text-slate-400">{formatCurrency(realizado)}</p>
        </div>
      )}
    </div>
  );
};
